// AddStockFlow — the quick-actions "Add stock" journey from ANY page: pick a
// catalogue product, capture batch/expiry/qty/bin in the existing
// DirectReceiveModal, then stage + commit in one shot (the ledger-writing
// Direct Import path — same audit trail as the Products page flow).
import { useState } from 'react';
import { Icon } from '@/foundation/icons';
import { Modal } from '@/foundation/primitives';
import { useStore } from '@/store/store';
import { useBins } from '@/features/inventory/api';
import { useOpenOwings } from '@/features/inventory/useOpenOwings';
import { infoBox } from '@/features/inventory/components';
import type { Product } from '@/domain/types';
import { ProductPicker } from './ProductPicker';
import { DirectReceiveModal, type DraftLine } from './DirectReceiveModal';
import { newManualDraft } from './manualDraft';
import { useCommitStaged, useStageDirectImport } from './api';

export function AddStockFlow({
  storeId,
  onClose,
  initialProduct,
}: {
  storeId: string;
  onClose: () => void;
  /** Pre-seed the flow with a known product (e.g. launched from a product page) — skips the
   *  catalogue picker and goes straight to batch/expiry/qty entry for that product. */
  initialProduct?: Product;
}) {
  const { toast } = useStore();
  const { data: bins = [] } = useBins();
  const stage = useStageDirectImport();
  const commit = useCommitStaged();
  const [draft, setDraft] = useState<DraftLine | null>(
    initialProduct ? newManualDraft(initialProduct) : null,
  );
  const owings = useOpenOwings(draft ? { sku: draft.sku } : undefined, storeId);

  const onSave = async (line: DraftLine) => {
    try {
      const staged = await stage.mutateAsync({
        store_id: storeId,
        sku: line.sku,
        gtin: line.gtin,
        product_snapshot_id: line.product_snapshot_id,
        controlled: line.controlled,
        is_fridge: line.is_fridge,
        batch_number: line.batch_number,
        expiry_date: line.expiry_date,
        expiry_is_default: line.expiry_is_default ?? false,
        qty_packs: line.qty_packs,
        units_per_pack: line.units_per_pack,
        bin_allocations: line.bin_allocations,
        capture_source: 'manual',
      });
      const res = await commit.mutateAsync({ store_id: storeId, ids: [staged.id] });
      if (res.committed_count > 0) {
        toast(`${line.product_name || line.sku} added to stock`, 'success');
        onClose();
      } else {
        const failure = res.failed?.[0];
        toast(failure?.error_message || 'Could not add to stock', 'error');
      }
    } catch (err) {
      const body = (err as { body?: { detail?: string; errors?: Record<string, string> } }).body;
      const msg = body?.errors
        ? Object.values(body.errors)[0]
        : body?.detail || 'Could not add to stock';
      toast(String(msg), 'error');
    }
  };

  if (draft) {
    return (
      <DirectReceiveModal
        initial={draft}
        bins={bins}
        storeId={storeId}
        onClose={onClose}
        onSave={(line) => void onSave(line)}
        notice={
          owings.count > 0 ? (
            <div style={{ ...infoBox, background: 'var(--warning-soft)' }}>
              <Icon name="clock" size={18} color="var(--warning-dark)" />
              <span>
                <b>
                  {owings.count} patient{owings.count > 1 ? 's are' : ' is'} owing this product
                </b>{' '}
                ({owings.pendingUnits} unit{owings.pendingUnits > 1 ? 's' : ''} pending) — adding
                stock marks their backorder fulfillable.
              </span>
            </div>
          ) : undefined
        }
      />
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      width={520}
      title="Add stock — pick a product"
      subtitle="Search the catalogue, then enter batch, expiry, quantity & bin. Commits straight to stock."
    >
      <ProductPicker onPick={(p) => setDraft(newManualDraft(p))} />
    </Modal>
  );
}
